import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import ConsultantTermsDialog from "../../modals/ConsultantTermsDialog";

const ConsultantOnboardingPage = ({ handleAddProfile }) => {
  const user = useSelector((state) => state.user.user);
  const [tncOpen, setTncOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: user?.Name || "",
    email: user?.Email || "",
    mobileNumber: user?.MobileNumber || "",
    companyName: "",
    professionalWebsite: "",
    yearsOfExperience: "",
    loanSpecialization: [],
    otherSpecialization: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showOtherField, setShowOtherField] = useState(false);

  const experienceOptions = [
    "1-3 years",
    "3-5 years",
    "5-10 years",
    "10+ years",
  ];
  const specializationOptions = [
    "Retail Loans (HL, Education Loan etc)",
    "Loan Against Property",
    "Working Capital Loans (MSME/SME)",
    "Project Financing",
    "Equipment financing",
    "Wholesale Lending",
    "Other",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;

    if (value === "Other") {
      setShowOtherField(checked);
    }

    setFormData((prev) => {
      const updated = checked
        ? [...prev.loanSpecialization, value]
        : prev.loanSpecialization.filter((item) => item !== value);
      return {
        ...prev,
        loanSpecialization: updated,
        ...(value === "Other" && !checked ? { otherSpecialization: "" } : {}),
      };
    });
  };

  const isFormValid = () =>
    formData.name.trim() !== "" &&
    /\S+@\S+\.\S+/.test(formData.email) &&
    formData.mobileNumber.trim() !== "" &&
    formData.yearsOfExperience !== "" &&
    formData.loanSpecialization.length > 0 &&
    termsAccepted;

  const handleSubmit = async () => {
    const errors = {};

    if (!formData.yearsOfExperience) {
      errors.yearsOfExperience = "Years of experience is required";
    }

    setFormErrors(errors);
    if (!isFormValid()) return;
    // setLoading(true);
    let submissionData = { ...formData, id: user?.ID, userType: "consultant" };
    try {
      handleAddProfile(submissionData);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAgree = () => {
    setTermsAccepted(true);
    setTncOpen(false);
    // alert("You agreed to the Terms & Conditions!");
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
          Basic Details
        </Typography>
        <Grid container spacing={2}>
          {/* Personal Fields */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={
                <span>
                  Full Name <span style={{ color: "red" }}>*</span>
                </span>
              }
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              disabled
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={formData.email && !/\S+@\S+\.\S+/.test(formData.email)}
              helperText={
                formData.email &&
                !/\S+@\S+\.\S+/.test(formData.email) &&
                "Enter a valid email"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              disabled
              label="Mobile Number"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company/Firm Name (if applicable)"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Professional Website (if any)"
              name="professionalWebsite"
              value={formData.professionalWebsite}
              onChange={handleChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          {/* Professional Fields */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={
                !formData.yearsOfExperience && formErrors.yearsOfExperience
              } // show error state
            >
              <InputLabel>
                Years of Experience <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <Select
                name="yearsOfExperience"
                value={formData.yearsOfExperience}
                onChange={handleChange}
                label="Years of Experience"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: 2, // makes the border itself curved
                  },
                }}
              >
                {experienceOptions.map((exp) => (
                  <MenuItem key={exp} value={exp}>
                    {exp}
                  </MenuItem>
                ))}
              </Select>
              {!formData.yearsOfExperience && formErrors.yearsOfExperience && (
                <FormHelperText>{formErrors.yearsOfExperience}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Specialization Section */}
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                p: 2,
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Primary Loan Specialization(s)
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {specializationOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        value={option}
                        checked={formData.loanSpecialization.includes(option)}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label={option}
                  />
                ))}
              </Box>
              {showOtherField && (
                <TextField
                  fullWidth
                  name="otherSpecialization"
                  label="Please specify"
                  value={formData.otherSpecialization}
                  onChange={handleChange}
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          </Grid>

          {/* Terms & Conditions */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
              }
              label={
                <span>
                  I accept the{" "}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setTncOpen(true)}
                    sx={{ textTransform: "none", p: 0 }}
                  >
                    Terms & Conditions
                  </Button>
                </span>
              }
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!isFormValid() || loading}
                sx={{
                  backgroundColor: "#003A91",
                  "&:hover": { backgroundColor: "#002570" },
                  height: "40px",
                  textTransform: "none",
                }}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Terms & Conditions Section */}
      {tncOpen && (
        <ConsultantTermsDialog
          open={tncOpen}
          onClose={() => setTncOpen(false)}
          onAgree={handleAgree}
        />
      )}
    </Box>
  );
};

export default ConsultantOnboardingPage;
